import { useGetLatestDataEntryVersionQuery } from 'features/entry-form-v2/data/useGetLatestDataEntryVersionQuery';
import { useLanguage } from 'features/hooks/useLanguage';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { LanguageType } from 'types/enums';
import { Dialog } from '../../component/Dialog';
import { ROUTE_MAP } from '../../utils/routeMap';
import { useCreateSurveyInvitationMutation } from '../data/useCreateSurveyInvitationMutation';
import { SurveyCreationForm } from './SurveyCreationForm';
import { SurveyDistributionView } from './SurveyDistributionView';

export const CreateSurveyInvitationModal = ({
	open,
	onClose,
	seriesName
}: {
	open: boolean;
	onClose: () => void;
	seriesName: string;
}) => {
	const params = useParams();

	const language = useLanguage();
	const dictionary = DICTIONARY[language];

	const [surveyId, setSurveyId] = useState<string>();
	const [pinCode, setPinCode] = useState<string>();

	const surveyLink = surveyId
		? `${window.location.origin}${ROUTE_MAP.surveys.byId.createPath({ surveyId })}`
		: '';

	const entryId = params.entryId as string;
	const projectId = params.projectId as string;

	const latestEntryVersionQuery = useGetLatestDataEntryVersionQuery({
		entryId,
		projectId
	});

	const originalEntryId = latestEntryVersionQuery.data?.entry['original_id'];

	const createSurveyMutation = useCreateSurveyInvitationMutation();

	const resetState = () => {
		setSurveyId(undefined);
		setPinCode(undefined);
	};

	const handleClose = () => {
		resetState();
		onClose();
	};

	const handleCreateSurvey = async (formId: string, surveyName: string) => {
		const result = await createSurveyMutation.mutateAsync({
			projectId: parseInt(projectId),
			entryId: originalEntryId,
			seriesName,
			formId: parseInt(formId),
			surveyName
		});

		setSurveyId(result.surveyId);
		setPinCode(result.pinCode);
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			hideCloseButton={true}
			title={!surveyId ? dictionary.createSurvey : dictionary.sendSurvey}
		>
			<div className="flex flex-col w-[500px]">
				<div className="flex flex-col gap-4">
					<SurveyModalContent
						surveyId={surveyId}
						pinCode={pinCode}
						seriesName={seriesName}
						projectId={projectId}
						surveyLink={surveyLink}
						onCancel={handleClose}
						onSubmit={handleCreateSurvey}
						onClose={handleClose}
						isLoading={createSurveyMutation.isLoading}
					/>
				</div>
			</div>
		</Dialog>
	);
};

interface SurveyModalContentProps {
	surveyId: string | undefined;
	pinCode: string | undefined;
	seriesName: string;
	projectId: string;
	surveyLink: string;
	onCancel: () => void;
	onSubmit: (formId: string, surveyName: string) => Promise<void>;
	onClose: () => void;
	isLoading: boolean;
}

const SurveyModalContent = ({
	surveyId,
	pinCode,
	seriesName,
	projectId,
	surveyLink,
	onCancel,
	onSubmit,
	onClose,
	isLoading
}: SurveyModalContentProps) => {
	if (!surveyId) {
		return (
			<SurveyCreationForm
				seriesName={seriesName}
				projectId={projectId}
				onCancel={onCancel}
				onSubmit={onSubmit}
				isLoading={isLoading}
			/>
		);
	}

	if (pinCode) {
		return (
			<SurveyDistributionView surveyLink={surveyLink} pinCode={pinCode} onClose={onClose} />
		);
	}

	return null;
};

const DICTIONARY = {
	[LanguageType.English]: {
		createSurvey: 'Create survey',
		sendSurvey: 'Send survey'
	},
	[LanguageType.Norwegian]: {
		createSurvey: 'Lag undersøkelse',
		sendSurvey: 'Send ut undersøkelse'
	}
};
